import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, User, AlertCircle, CheckSquare, Square, ShieldCheck, Zap, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { ForgotPasswordModal } from '../components/ForgotPasswordModal';
import { AnimatedLogo } from '../components/AnimatedLogo';
import { ToolShowcase } from '../components/ToolShowcase';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register, loginWithGoogle } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Vérification du consentement pour l'inscription
    if (!isLogin && !acceptedTerms) {
      setError('Vous devez accepter les CGU et confirmer votre statut de professionnel de santé pour créer un compte.');
      return;
    }

    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData.email, formData.password, formData.full_name);
      }

      if (result.success) {
        toast.success(isLogin ? 'Connexion réussie!' : 'Compte créé avec succès!');
        navigate('/dashboard');
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      setError('Une erreur est survenue');
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-indigo-600/20" />
        <img
          src="https://images.unsplash.com/photo-1680792563719-288027b2a090?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Medical background"
          className="w-full h-full object-cover"
        />
        {/* Scrim sombre pour contraste et lisibilité du texte héros */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/65 to-indigo-950/80" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center max-w-xl">
            <div className="flex justify-center mb-4">
              <AnimatedLogo size={132} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Hippocrate MG
            </h2>

            {/* Objectif du service — accroche percutante */}
            <p className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-2" data-testid="hero-mission-headline">
              Voir clair. Décider d'un trait.
            </p>
            <p className="text-sm text-slate-200/90 leading-relaxed mb-4" data-testid="hero-mission-sub">
              L'assistant documentaire qui place la <span className="text-white font-semibold">médecine fondée sur les preuves</span> au cœur de votre consultation — pour tous les médecins.
            </p>

            {/* Preuves de valeur */}
            <div className="flex flex-wrap justify-center gap-2 mb-5" data-testid="hero-value-chips">
              {[
                { icon: ShieldCheck, label: 'Sources officielles · HAS, Ameli' },
                { icon: Zap, label: 'Réponses en secondes' },
                { icon: BadgeCheck, label: 'Zéro invention, tout est sourcé' },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1.5 text-xs font-medium text-slate-100"
                >
                  <Icon className="w-3.5 h-3.5 text-cyan-300" /> {label}
                </span>
              ))}
            </div>

            {/* Aperçu animé des outils */}
            <ToolShowcase />

            {/* Mention de gratuité - conforme CGU */}
            <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-3 max-w-md mx-auto border border-slate-700">
              <p className="text-xs text-slate-400 leading-relaxed">
                Service mis à disposition à titre gratuit par le <span className="text-slate-200 font-medium">Dr STEPANIAN Tigran</span> en phase de lancement. La gratuité est consentie sans garantie de pérennité.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.png" 
              alt="Hippocrate MG" 
              className="w-40 h-40 mx-auto mb-4 object-contain"
            />
            <h2 className="text-2xl font-bold text-white">Hippocrate MG</h2>
            <p className="text-sm text-slate-400 mt-1" data-testid="hero-mission-mobile">
              Voir clair. Décider d'un trait. La médecine fondée sur les preuves, en quelques secondes.
            </p>
          </div>

          <div className="glassmorphic rounded-2xl p-8">
            {/* Service réservé - mention obligatoire */}
            <div className="bg-blue-900/30 border border-blue-800/50 rounded-lg px-4 py-3 mb-6">
              <p className="text-xs text-blue-300 text-center font-medium">
                Service réservé aux professionnels de santé
              </p>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">
              {isLogin ? 'Connexion' : 'Créer un compte'}
            </h2>
            <p className="text-slate-400 mb-6">
              {isLogin ? 'Accédez à l\'outil documentaire médical' : 'Inscription à l\'outil documentaire'}
            </p>

            {error && (
              <div data-testid="error-message" className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-rose-400">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nom complet
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      data-testid="input-fullname"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Dr. Jean Dupont"
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email professionnel
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="medecin@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    data-testid="input-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {/* Checkbox obligatoire pour l'inscription - CGU Articles 9 et 10 */}
              {!isLogin && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className="flex items-start gap-3 text-left w-full group"
                    data-testid="terms-checkbox"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {acceptedTerms ? (
                        <CheckSquare className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-500 group-hover:text-slate-400" />
                      )}
                    </div>
                    <span className="text-xs text-slate-400 leading-relaxed">
                      Je déclare être un <span className="text-slate-300">professionnel de santé dûment habilité</span>, et j'accepte sans réserve les{' '}
                      <Link 
                        to="/mentions-legales" 
                        className="text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                      >
                        CGU
                      </Link>
                      {' '}et la{' '}
                      <Link 
                        to="/confidentialite" 
                        className="text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                      >
                        Politique de confidentialité
                      </Link>.
                    </span>
                  </button>
                </div>
              )}

              <button
                data-testid="submit-button"
                type="submit"
                disabled={loading || (!isLogin && !acceptedTerms)}
                className="w-full py-3 px-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all shadow-[0_4px_14px_0_rgba(59,130,246,0.39)] hover:shadow-[0_6px_20px_rgba(59,130,246,0.5)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? 'Chargement...' : (isLogin ? 'Se connecter' : 'Créer un compte')}
              </button>

              {isLogin && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    Mot de passe oublié?
                  </button>
                </div>
              )}
            </form>

            {/* Séparateur */}
            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-slate-800" />
              <span className="text-xs text-slate-500">ou</span>
              <div className="h-px flex-1 bg-slate-800" />
            </div>

            {/* Connexion Google (Emergent Auth) */}
            <button
              data-testid="google-login-button"
              type="button"
              onClick={loginWithGoogle}
              className="w-full py-3 px-6 rounded-full bg-white hover:bg-slate-100 text-slate-800 font-medium transition-all flex items-center justify-center gap-3 shadow-sm hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
              </svg>
              {isLogin ? 'Continuer avec Google' : "S'inscrire avec Google"}
            </button>

            <p className="mt-3 text-center text-[11px] text-slate-500">
              En continuant avec Google, vous déclarez être un professionnel de santé et acceptez les{' '}
              <Link to="/mentions-legales" className="text-blue-400 hover:underline" target="_blank">CGU</Link>.
            </p>
            <div className="mt-6 text-center">
              <button
                data-testid="toggle-auth-mode"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setAcceptedTerms(false);
                  setFormData({ email: '', password: '', full_name: '' });
                }}
                className="text-sm text-slate-400 hover:text-blue-400 transition-colors"
              >
                {isLogin ? 'Pas encore de compte? Créer un compte' : 'Déjà un compte? Se connecter'}
              </button>
            </div>

            {/* Liens légaux accessibles AVANT création de compte */}
            <div className="mt-6 pt-4 border-t border-slate-800 flex justify-center gap-4 text-xs">
              <Link to="/blog" className="text-slate-500 hover:text-blue-400 transition-colors" data-testid="login-blog-link">
                Blog
              </Link>
              <Link to="/mentions-legales" className="text-slate-500 hover:text-blue-400 transition-colors" target="_blank">
                CGU
              </Link>
              <Link to="/confidentialite" className="text-slate-500 hover:text-blue-400 transition-colors" target="_blank">
                Confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};
